import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, styled } from "@mui/material";
import { useFormikContext } from "formik";
import { getCountries, getStates, getRelationList } from "../../../../../api";
import AddRelation from "../../../../../QuickAdd/AddRelation";
import {
  FieldRow,
  CustomSelect,
} from "../../../../../HelperFunctions/EmployeeFormHelpers";
import { enqueueSnackbar } from "notistack";
import employeeManagementStyles from "../../../../../styles/employeeManagement.styles";

/* =======================
   Styled Components
======================= */

const StyledFieldset = styled("fieldset")({
  border: "0.063rem solid #ced4da",
  borderRadius: "0.5rem",
  padding: "0.5rem 1.25rem 0.75rem 1.25rem",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  maxWidth: "42rem",
  backgroundColor: "#ffffff",
});

const StyledLegend = styled("legend")({
  padding: "0 0.5rem",
  fontSize: "0.75rem",
  fontWeight: "bold",
  color: "#333",
});

// --- Validation Helpers ---
const validateCityInput = (val) => {
  if (val.length > 50) return "City cannot exceed 50 characters.";
  if (/[^a-zA-Z\s]/.test(val)) return "City must contain only alphabets.";
  return null;
};

const validateZipCodeInput = (val) => {
  if (/[^0-9]/.test(val)) return "Zip Code must be numeric.";
  return null;
};

const validateZipCodeBlur = (val) => {
  if (val.length > 0 && (val.length < 5 || val.length > 10)) return "Zip Code must be 5–10 digits.";
  return null;
};

const validateEmailFormat = (email) => {
  if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
    return "Invalid email address in Emergency Contact.";
  }
  return null;
};

const validatePhoneNumber = (val) => {
  const digits = String(val || "").replace(/\D/g, "");
  if (digits.length > 0 && digits.length < 10) return "must be 10 digits";
  return null;
};

// --- Utilities ---
const findNewlyAddedRelation = (updatedRelations, oldRelations, resData) => {
  let newlyAdded = null;
  if (resData && typeof resData === "string") {
    newlyAdded = updatedRelations.find((r) => r.label?.toLowerCase() === resData.toLowerCase());
  } else if (resData && resData.Name) {
    newlyAdded = updatedRelations.find((r) => r.label?.toLowerCase() === resData.Name.toLowerCase());
  } else if (resData && resData.name) {
    newlyAdded = updatedRelations.find((r) => r.label?.toLowerCase() === resData.name.toLowerCase());
  }

  if (!newlyAdded) {
    newlyAdded = updatedRelations.find((ur) => !oldRelations.some((or) => or.value === ur.value));
  }
  return newlyAdded;
};

/* =======================
   Main Component
======================= */

const AddressDetails = () => {
  const {
    values,
    setFieldValue,
    handleBlur,
    setFieldTouched,
    setFieldError,
    handleChange,
    resetForm,
    dirty,
  } = useFormikContext();

  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const [countries, setCountries] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [relations, setRelations] = useState([]);
  const [openAddRelation, setOpenAddRelation] = useState(false);
  const [filteredStates, setFilteredStates] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [relationLoading, setRelationLoading] = useState(false);

  // --- Handlers: Phone Validation ---
  const handlePhoneChange = useCallback((e, label) => {
    if (/^[0-9]*$/.test(e.target.value)) {
      handleChange(e);
    } else {
      enqueueSnackbar(`${label} must be numeric.`, { variant: "warning" });
    }
  }, [handleChange]);

  const handlePhoneBlur = useCallback((e, fieldName, label) => {
    handleBlur(e);
    const error = validatePhoneNumber(e.target.value);
    if (error) {
      setFieldValue(fieldName, "");
      setFieldTouched(fieldName, true, false);
      enqueueSnackbar(`${label} ${error}`, { variant: "error" });
    } else {
      setFieldError(fieldName, undefined);
    }
  }, [handleBlur, setFieldValue, setFieldTouched, setFieldError]);

  /* ===== Fetch APIs once ===== */
  useEffect(() => {
    const fetchData = async () => {
      setGeoLoading(true);
      try {
        const [countriesRes, statesRes] = await Promise.all([
          getCountries(),
          getStates(),
        ]);

        setCountries(countriesRes);
        setAllStates(statesRes);
      } catch (error) {
        console.error("Address API Error:", error.message);
      } finally {
        setGeoLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchRelationsList = useCallback(async () => {
    setRelationLoading(true);
    try {
      const relationData = await getRelationList();
      setRelations(relationData);
      return relationData;
    } catch (err) {
      console.error("Relation Fetch Error:", err.message);
      return [];
    } finally {
      setRelationLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRelationsList();
  }, [fetchRelationsList]);

  const handleAddRelationSuccess = useCallback(async (resData) => {
    const oldRelations = relations;
    const updatedRelations = await fetchRelationsList();
    const newlyAdded = findNewlyAddedRelation(updatedRelations, oldRelations, resData);

    if (newlyAdded) {
      setFieldValue("emergencyContact.relationId", newlyAdded.value);
      setFieldValue("emergencyContact.relationName", newlyAdded.label);
    }
  }, [relations, fetchRelationsList, setFieldValue]);

  /* ===== Default Country to USA ===== */
  useEffect(() => {
    if (countries.length > 0 && !values.addressDetails?.countryId && !values.id) {
      const usa = countries.find(
        (c) => c.label === "USA" || c.label === "UNITED STATES" || c.label === "UNITED STATES OF AMERICA",
      );
      if (usa) {
        if (!dirty) {
          resetForm({
            values: {
              ...valuesRef.current,
              addressDetails: {
                ...valuesRef.current?.addressDetails,
                countryId: usa.value,
                countryName: usa.label,
                countryCode: usa.refCode,
              },
            },
          });
        } else {
          setFieldValue("addressDetails.countryId", usa.value);
          setFieldValue("addressDetails.countryName", usa.label);
          setFieldValue("addressDetails.countryCode", usa.refCode);
        }
      }
    }
  }, [countries, values.addressDetails?.countryId, values.id, dirty, resetForm, setFieldValue]);

  /* ===== Filter states when country is loaded ===== */
  useEffect(() => {
    if (values.addressDetails?.countryId && allStates.length > 0) {
      const statesByCountry = allStates.filter(
        (s) => String(s.refid) === String(values.addressDetails.countryId),
      );
      setFilteredStates(statesByCountry);
    } else {
      setFilteredStates([]);
    }
  }, [values.addressDetails?.countryId, allStates]);

  /* ===== Set relation name when relationId is loaded ===== */
  useEffect(() => {
    if (values.emergencyContact?.relationId && relations.length > 0) {
      const relation = relations.find(
        (r) => r.value === values.emergencyContact.relationId,
      );
      if (relation && relation.label !== values.emergencyContact.relationName) {
        if (!dirty) {
          resetForm({
            values: {
              ...valuesRef.current,
              emergencyContact: {
                ...valuesRef.current?.emergencyContact,
                relationName: relation.label || "",
              },
            },
          });
        } else {
          setFieldValue("emergencyContact.relationName", relation.label || "");
        }
      }
    }
  }, [values.emergencyContact?.relationId, values.emergencyContact?.relationName, relations, dirty, resetForm, setFieldValue]);

  // --- Handlers: Input Fields ---
  const handleCityChange = useCallback((e) => {
    const val = e.target.value;
    const error = validateCityInput(val);
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
    } else {
      setFieldValue("addressDetails.city", val);
    }
  }, [setFieldValue]);

  const handleZipChange = useCallback((e) => {
    const val = e.target.value;
    const error = validateZipCodeInput(val);
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
    } else {
      setFieldValue("addressDetails.zipCode", val);
    }
  }, [setFieldValue]);

  const handleZipBlur = useCallback((e) => {
    handleBlur(e);
    const error = validateZipCodeBlur(e.target.value);
    if (error) {
      setFieldValue("addressDetails.zipCode", "");
      setFieldTouched("addressDetails.zipCode", true, false);
      enqueueSnackbar(error, { variant: "error" });
    } else {
      setFieldError("addressDetails.zipCode", undefined);
    }
  }, [handleBlur, setFieldValue, setFieldTouched, setFieldError]);

  const handleCountryChange = useCallback((e) => {
    const countryId = e.target.value;
    const country = countries.find((c) => c.value === countryId);

    setFieldValue("addressDetails.countryId", country?.value || "");
    setFieldValue("addressDetails.countryName", country?.label || "");
    setFieldValue("addressDetails.countryCode", country?.refCode || "");

    const statesByCountry = allStates.filter((s) => String(s.refid) === String(country?.value));
    setFilteredStates(statesByCountry);

    setFieldValue("addressDetails.stateId", "");
    setFieldValue("addressDetails.stateName", "");
    setFieldValue("addressDetails.stateCode", "");
  }, [countries, allStates, setFieldValue]);

  const handleStateChange = useCallback((e) => {
    const stateId = e.target.value;
    const state = filteredStates.find((s) => s.value === stateId);

    setFieldValue("addressDetails.stateId", state?.value || "");
    setFieldValue("addressDetails.stateName", state?.label || "");
    setFieldValue("addressDetails.stateCode", state?.refCode || "");
  }, [filteredStates, setFieldValue]);

  const handleRelationChange = useCallback((e) => {
    const value = e.target.value;
    if (value === "ADD_NEW") {
      setOpenAddRelation(true);
      return;
    }
    const rel = relations.find((r) => r.value === value);
    setFieldValue("emergencyContact.relationId", rel?.value || "");
    setFieldValue("emergencyContact.relationName", rel?.label || "");
  }, [relations, setFieldValue]);

  const handleEmailBlur = useCallback((e) => {
    handleBlur(e);
    const email = e.target.value;
    const error = validateEmailFormat(email);
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
    } else {
      setFieldError("emergencyContact.emailId", undefined);
    }
  }, [handleBlur, setFieldError]);
  // console.log("Formik CountryId:", values.addressDetails.countryId);

  return (
      <Box sx={employeeManagementStyles.addressDetailsContainer}>
        <Box sx={employeeManagementStyles.addressDetailsRow}>
          {/* ===== Main Address ===== */}
          <StyledFieldset>
            <StyledLegend>Main Address</StyledLegend>

            <Box sx={employeeManagementStyles.fieldsetColumn}>
              <FieldRow
                heading="Address"
                name="addressDetails.address"
                labelWidth={100}
              />
              <FieldRow
                heading="City"
                name="addressDetails.city"
                labelWidth={100}
                onChange={handleCityChange}
              />

              {/* Country */}
              <CustomSelect
                heading="Country"
                name="addressDetails.countryId"
                loading={geoLoading}
                value={String(values.addressDetails.countryId ?? "")}
                options={[{ value: "", label: "--Select--" }, ...countries]}
                labelWidth={100}
                onChange={handleCountryChange}
              />

              <FieldRow
                heading="Zipcode"
                name="addressDetails.zipCode"
                labelWidth={100}
                inputProps={{ maxLength: 10 }}
                onChange={handleZipChange}
                onBlur={handleZipBlur}
              />

              {/* State */}
              <CustomSelect
                heading="State"
                name="addressDetails.stateId"
                loading={geoLoading}
                value={values.addressDetails.stateId || ""}
                options={[
                  { value: "", label: "--Select--" },
                  ...filteredStates,
                ]}
                labelWidth={100}
                onChange={handleStateChange}
              />
            </Box>
          </StyledFieldset>

          {/* ===== Emergency Contact ===== */}
          <StyledFieldset>
            <StyledLegend>Emergency Contact Details</StyledLegend>

            <Box sx={employeeManagementStyles.fieldsetColumn}>
              <FieldRow
                heading="Name"
                name="emergencyContact.name"
                labelWidth={100}
              />
              <CustomSelect
                heading="Relation"
                name="emergencyContact.relationId"
                loading={relationLoading}
                value={values.emergencyContact.relationId || ""}
                options={[
                  { value: "", label: "--Select--" },
                  { value: "ADD_NEW", label: "Add New" },
                  ...relations,
                ]}
                labelWidth={100}
                onChange={handleRelationChange}
              />
              <FieldRow
                heading="Mobile Num"
                name="emergencyContact.mobileNum"
                labelWidth={100}
                onChange={(e) => handlePhoneChange(e, "Mobile Number")}
                inputProps={{ maxLength: 10 }}
                onBlur={(e) =>
                  handlePhoneBlur(
                    e,
                    "emergencyContact.mobileNum",
                    "Mobile Number",
                  )
                }
              />
              <FieldRow
                heading="Work Num"
                name="emergencyContact.workNum"
                labelWidth={100}
                onChange={(e) => handlePhoneChange(e, "Work Number")}
                inputProps={{ maxLength: 10 }}
                onBlur={(e) =>
                  handlePhoneBlur(e, "emergencyContact.workNum", "Work Number")
                }
              />
              <FieldRow
                heading="Fax Num"
                name="emergencyContact.faxNum"
                labelWidth={100}
                onChange={(e) => handlePhoneChange(e, "Fax Number")}
                inputProps={{ maxLength: 10 }}
                onBlur={(e) =>
                  handlePhoneBlur(e, "emergencyContact.faxNum", "Fax Number")
                }
              />
              <FieldRow
                heading="Email ID"
                name="emergencyContact.emailId"
                labelWidth={100}
                onBlur={handleEmailBlur}
              />
            </Box>
          </StyledFieldset>
        </Box>
        <AddRelation
          open={openAddRelation}
          onClose={() => setOpenAddRelation(false)}
          onSuccess={handleAddRelationSuccess}
        />
      </Box>
  );
};

export default AddressDetails;
